import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { CONVERSION_TYPES, ConversionType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import ConversionContactTable from 'app/components/analytics/ConversionContactTable'
import { ConversionMetricsResponse } from 'types/analytics'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { OptionType } from 'types/OptionType'
import { logError } from 'lib/logging'
import { gqlQuery } from 'app/lib/graphqlClient'
import { graphql } from 'app/generated/gql'

const CONVERSION_METRICS_QUERY = graphql(`
  query ConversionsDashboardMetrics($dateRange: DateRangeInput!, $locationIds: [String!]) {
    conversionMetrics(dateRange: $dateRange, locationIds: $locationIds) {
      total
      QUALIFIED_LEAD_GENERATED {
        total
        failed
      }
      NEW_PATIENT_APPOINTMENT_SCHEDULED {
        total
        failed
      }
      NEW_PATIENT_GENERATED {
        total
        failed
      }
      ADDITIONAL_TREATMENT_REVENUE {
        total
        failed
      }
    }
  }
`)

type ConversionStageMetrics = { total: number; failed: number }

type ConversionMetricsGqlResponse = {
  total: number
  QUALIFIED_LEAD_GENERATED: ConversionStageMetrics
  NEW_PATIENT_APPOINTMENT_SCHEDULED: ConversionStageMetrics
  NEW_PATIENT_GENERATED: ConversionStageMetrics
  ADDITIONAL_TREATMENT_REVENUE: ConversionStageMetrics
}

export const STAGE_LABELS: Record<ConversionType, string> = {
  QUALIFIED_LEAD_GENERATED: `Qualified Leads`,
  NEW_PATIENT_APPOINTMENT_SCHEDULED: `Appointments Scheduled`,
  NEW_PATIENT_GENERATED: `New Patients`,
  ADDITIONAL_TREATMENT_REVENUE: `Additional Revenue`,
}

export type UseConversionsDashboardReturn = {
  fromDate: string
  toDate: string
  locationOptions: OptionType[]
  selectedLocations: OptionType[]
  metrics: ConversionMetricsResponse | undefined
  isLoadingMetrics: boolean
  onDateChange: (fromDate: string, toDate: string) => void
  onLocationChange: (options: OptionType[]) => void
  onStageClick: (conversionType: ConversionType) => void
}

export function useConversionsDashboard(): UseConversionsDashboardReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  const fromDate = searchParams.get(`fromDate`) || moment().subtract(90, `days`).format(`YYYY-MM-DD`)
  const toDate = searchParams.get(`toDate`) || moment().format(`YYYY-MM-DD`)

  const locationIdsParam = searchParams.get(`locationIds`)
  const locationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])

  const [metrics, setMetrics] = useState<ConversionMetricsResponse>()
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false)
  const { locationOptions } = useLocationIntegrationStatus()

  useEffect(() => {
    if (!fromDate || !toDate) return
    setIsLoadingMetrics(true)
    gqlQuery(CONVERSION_METRICS_QUERY, {
      dateRange: { fromDate, toDate },
      locationIds: locationIds.length ? locationIds : undefined,
    })
      .then(({ data, error }) => {
        if (error) {
          logError(`Error fetching conversion metrics`, error.message)
          return
        }
        if (data?.conversionMetrics) {
          const nextMetrics = data.conversionMetrics
          setMetrics({
            total: nextMetrics.total,
            stages: {
              QUALIFIED_LEAD_GENERATED: nextMetrics.QUALIFIED_LEAD_GENERATED,
              NEW_PATIENT_APPOINTMENT_SCHEDULED: nextMetrics.NEW_PATIENT_APPOINTMENT_SCHEDULED,
              NEW_PATIENT_GENERATED: nextMetrics.NEW_PATIENT_GENERATED,
              ADDITIONAL_TREATMENT_REVENUE: nextMetrics.ADDITIONAL_TREATMENT_REVENUE,
            },
          })
        }
      })
      .catch((err) => logError(`Error fetching conversion metrics`, err))
      .finally(() => setIsLoadingMetrics(false))
  }, [fromDate, toDate, locationIdsParam])

  const onDateChange = (newFromDate: string, newToDate: string) => {
    setSearchParams((prev) => {
      prev.set(`fromDate`, newFromDate)
      prev.set(`toDate`, newToDate)
      return prev
    })
  }

  const onLocationChange = (options: OptionType[]) => {
    if (options.length === 0 || options[0]?.value === ``) {
      setSearchParams((prev) => {
        prev.delete(`locationIds`)
        return prev
      })
    } else {
      setSearchParams((prev) => {
        prev.set(`locationIds`, compact(options.map((o) => o.value)).join(`,`))
        return prev
      })
    }
  }

  const selectedLocations = useMemo(
    () => locationOptions.filter((o) => locationIds.includes(o.value)),
    [locationOptions, locationIds],
  )

  const handleCloseDetails = () => {
    setSearchParams((prev) => {
      prev.delete(`details`)
      prev.delete(`drawer`)
      return prev
    })
  }

  const onStageClick = (conversionType: ConversionType) => {
    setSearchParams((prev) => {
      prev.set(`details`, conversionType)
      return prev
    })
  }

  const detailsParam = searchParams.get(`details`)
  useEffect(() => {
    if (!detailsParam || !CONVERSION_TYPES.includes(detailsParam as ConversionType)) return
    const conversionType = detailsParam as ConversionType
    const timeoutId = setTimeout(() => {
      showModal(
        <ConversionContactTable
          title={STAGE_LABELS[conversionType]}
          conversionType={conversionType}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          onClose={closeModal}
        />,
        {
          hideTitle: true,
          isFullScreen: true,
          removePadding: true,
          onClose: handleCloseDetails,
          useRadix: true,
        },
      )
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [detailsParam, fromDate, toDate, locationIdsParam])

  return {
    fromDate,
    toDate,
    locationOptions,
    selectedLocations,
    metrics,
    isLoadingMetrics,
    onDateChange,
    onLocationChange,
    onStageClick,
  }
}
